import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  RateReview as ReviewIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import DocumentViewer from '../../../components/audit/DocumentViewer';
import { remapReviews } from '../../../helpers/utils';

const CaseDetails = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [auditCase, setAuditCase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [report, setReport] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status colors for chips
  const statusColors = {
    'preparation': 'default',
    'sent_to_client': 'info',
    'client_submitted': 'warning',
    'first_review': 'primary',
    'second_review': 'primary',
    'final_review': 'secondary',
    'draft_report': 'secondary',
    'completed': 'success'
  };

  // Status labels for display
  const statusLabels = {
    'preparation': 'Preparation',
    'sent_to_client': 'Sent to Client',
    'client_submitted': 'Client Submitted',
    'first_review': 'First Review',
    'second_review': 'Second Review',
    'final_review': 'Final Review',
    'draft_report': 'Draft Report',
    'completed': 'Completed'
  };

  // Workflow steps
  const workflowSteps = [
    { label: 'Preparation', value: 'preparation' },
    { label: 'Sent to Client', value: 'sent_to_client' },
    { label: 'Client Submitted', value: 'client_submitted' },
    { label: 'First Review', value: 'first_review' },
    { label: 'Second Review', value: 'second_review' },
    { label: 'Final Review', value: 'final_review' },
    { label: 'Draft Report', value: 'draft_report' },
    { label: 'Completed', value: 'completed' }
  ];

  // Fetch case details on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch case details
        const caseResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/cases/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        setAuditCase(caseResponse.data);
        
        // Fetch documents for this case
        const documentsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/documents?case_id=${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        setDocuments(documentsResponse.data);
        
        // Fetch reviews for this case
        const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reviews/${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        const reviews = remapReviews(reviewsResponse);
        console.log(reviews);
        
        setReviews(reviews);
     
        
        // Fetch report if it exists
        const reportsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/audit/reports?case_id=${caseId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (reportsResponse.data.length > 0) {
          setReport(reportsResponse.data[0]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching case details:', err);
        setError('Failed to load case details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [caseId]);

  // Get active step index based on case status
  const getActiveStep = () => {
    if (!auditCase) return 0;
    
    const statusIndex = workflowSteps.findIndex(step => step.value === auditCase.status);
    return statusIndex !== -1 ? statusIndex : 0;
  };

  // Handle document selection for viewing
  const handleViewDocument = (document) => {
    setSelectedDocument(document);
    setShowDocumentViewer(true);
  };

  // Handle navigation to review interface
  const handleReviewCase = () => {
    navigate(`/audit/cases/${caseId}/review`);
  };

  // Handle navigation to report generator
  const handleGenerateReport = () => {
    navigate(`/audit/cases/${caseId}/report`);
  };

  // Handle navigation back to cases list
  const handleBackToCases = () => {
    navigate('/audit/my-cases');
  };

  // Determine if user can review this case
  const canReview = () => {
    if (!auditCase || !user) return false;
    
    if (user.role === 'administrator') return true;
    
    if (auditCase.first_reviewer_id === user.id && auditCase.status === 'client_submitted') {
      return true;
    }
    
    if (auditCase.second_reviewer_id === user.id && auditCase.status === 'first_review') {
      return true;
    }
    
    return false;
  };

  // Determine if user can generate report for this case
  const canGenerateReport = () => {
    if (!auditCase || !user) return false;
    
    if (user.role === 'administrator') return true;
    
    if ((user.role === 'manager' || auditCase.manager_id === user.id) && 
        ['second_review', 'final_review', 'draft_report'].includes(auditCase.status)) {
      return true;
    }
    
    return false;
  };

  // Render case details
  const renderCaseDetails = () => {
    if (!auditCase) return null;
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Workflow Status
            </Typography>
            
            <Stepper activeStep={getActiveStep()} orientation="vertical" sx={{ mt: 2 }}>
              {workflowSteps.map((step, index) => (
                <Step key={step.value}>
                  <StepLabel>
                    <Typography variant="subtitle1">
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary">
                      {index === 0 && 'Case created and questionnaire prepared.'}
                      {index === 1 && 'Questionnaire sent to client for completion.'}
                      {index === 2 && 'Client has submitted responses and documents.'}
                      {index === 3 && 'First reviewer is reviewing the responses.'}
                      {index === 4 && 'Second reviewer is reviewing the responses.'}
                      {index === 5 && 'Manager is reviewing the findings.'}
                      {index === 6 && 'Report is being prepared.'}
                      {index === 7 && 'Case is completed and report sent to client.'}
                    </Typography>
                    
                    {index === getActiveStep() && (
                      <Box sx={{ mt: 2 }}>
                        {canReview() && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleReviewCase}
                            startIcon={<ReviewIcon />}
                            sx={{ mr: 1, mb: 1 }}
                          >
                            Review Case
                          </Button>
                        )}
                        
                        {canGenerateReport() && (
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleGenerateReport}
                            startIcon={<DescriptionIcon />}
                            sx={{ mb: 1 }}
                          >
                            Generate Report
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Documents
            </Typography>
            
            {documents.length === 0 ? (
              <Typography variant="body2">No documents uploaded yet.</Typography>
            ) : (
              <List>
                {documents.map(document => (
                  <ListItem 
                    key={document.id}
                    button
                    onClick={() => handleViewDocument(document)}
                  >
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={document.original_filename} 
                      secondary={`${(document.file_size / 1024).toFixed(2)} KB`} 
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="View Document">
                        <IconButton 
                          edge="end" 
                          onClick={() => handleViewDocument(document)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton 
                          edge="end" 
                          onClick={() => window.open(document.download_url, '_blank')}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
          
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Reviews
            </Typography>
            
            {reviews.length === 0 ? (
              <Typography variant="body2">No reviews submitted yet.</Typography>
            ) : (
              <Box>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      First Review
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {reviews.filter(r => r.review_type === 'first').length === 0 ? (
                      <Typography variant="body2">First review not submitted yet.</Typography>
                    ) : (
                      <List>
                        {reviews.filter(r => r.review_type === 'first').map(review => (
                          <ListItem key={review.id}>
                            <ListItemText 
                              primary={review.answer?.question_text || 'Unknown Question'} 
                              secondary={
                                <React.Fragment>
                                  <Typography variant="body2" component="span" display="block">
                                    <strong>Comment:</strong> {review.comment || 'No comment'}
                                  </Typography>
                                  {review.has_deficiency && (
                                    <Chip 
                                      label={`${review.deficiency_severity.toUpperCase()} Deficiency`} 
                                      color={
                                        review.deficiency_severity === 'high' ? 'error' : 
                                        review.deficiency_severity === 'medium' ? 'warning' : 'info'
                                      }
                                      size="small"
                                      sx={{ mt: 1, mr: 1 }}
                                    />
                                  )}
                                  {review.relates_to_engagements && (
                                    <Chip 
                                      label="Engagement Related" 
                                      color="primary"
                                      size="small"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </React.Fragment>
                              } 
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      Second Review
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {reviews.filter(r => r.review_type === 'second').length === 0 ? (
                      <Typography variant="body2">Second review not submitted yet.</Typography>
                    ) : (
                      <List>
                        {reviews.filter(r => r.review_type === 'second').map(review => (
                          <ListItem key={review.id}>
                            <ListItemText 
                              primary={review.answer?.question_text || 'Unknown Question'} 
                              secondary={
                                <React.Fragment>
                                  <Typography variant="body2" component="span" display="block">
                                    <strong>Comment:</strong> {review.comment || 'No comment'}
                                  </Typography>
                                  {review.has_deficiency && (
                                    <Chip 
                                      label={`${review.deficiency_severity.toUpperCase()} Deficiency`} 
                                      color={
                                        review.deficiency_severity === 'high' ? 'error' : 
                                        review.deficiency_severity === 'medium' ? 'warning' : 'info'
                                      }
                                      size="small"
                                      sx={{ mt: 1, mr: 1 }}
                                    />
                                  )}
                                  {review.relates_to_engagements && (
                                    <Chip 
                                      label="Engagement Related" 
                                      color="primary"
                                      size="small"
                                      sx={{ mt: 1 }}
                                    />
                                  )}
                                </React.Fragment>
                              } 
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Paper>
          
          {report && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Report
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Created:</strong> {new Date(report.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Status:</strong> {report.sent_at ? 'Sent to Client' : 'Draft'}
                </Typography>
                {report.sent_at && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Sent:</strong> {new Date(report.sent_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                {report.pdf_path && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(`${process.env.REACT_APP_API_URL}/audit/reports/${report.id}/download`, '_blank')}
                  >
                    Download PDF
                  </Button>
                )}
                
                {!report.sent_at && canGenerateReport() && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<EditIcon />}
                    onClick={handleGenerateReport}
                  >
                    Edit Report
                  </Button>
                )}
              </Box>
            </Paper>
          )}
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Case Details
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Title:</strong> {auditCase.title}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Status:</strong> <Chip 
                  label={statusLabels[auditCase.status]} 
                  color={statusColors[auditCase.status]} 
                  size="small" 
                />
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Review Period:</strong> {new Date(auditCase.review_period_start).toLocaleDateString()} - {new Date(auditCase.review_period_end).toLocaleDateString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Created:</strong> {new Date(auditCase.created_at).toLocaleString()}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              <BusinessIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Firm
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Name:</strong> {auditCase.firm?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Contact:</strong> {auditCase.firm?.contact_email}
              </Typography>
              {auditCase.firm?.phone && (
                <Typography variant="body2" gutterBottom>
                  <strong>Phone:</strong> {auditCase.firm?.phone}
                </Typography>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              <PersonIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Team
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Manager:</strong> {auditCase.manager?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>First Reviewer:</strong> {auditCase.first_reviewer?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Second Reviewer:</strong> {auditCase.second_reviewer?.name}
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              <DescriptionIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Questionnaire
            </Typography>
            
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Title:</strong> {auditCase.questionnaire?.title}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Questions:</strong> {auditCase.questionnaire?.questions?.length || 0}
              </Typography>
            </Box>
          </Paper>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBackToCases}
            >
              Back to Cases
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1">
              <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Case: {auditCase?.title}
            </Typography>
            <Chip 
              label={statusLabels[auditCase?.status]} 
              color={statusColors[auditCase?.status]} 
              size="medium" 
            />
          </Box>
          
          {renderCaseDetails()}
          
          {/* Document Viewer Dialog */}
          {showDocumentViewer && selectedDocument && (
            <DocumentViewer 
              document={selectedDocument} 
              open={showDocumentViewer}
              onClose={() => setShowDocumentViewer(false)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CaseDetails;
